import HasID from "@/models/HasID";
import HasLabel from "@/models/HasLabel";
import TextUtils from "@/util/TextUtils";

export interface PatientPlan extends HasID {
    patientId: number | string,
    planId: number | string,
    memberId: number | string,
    groupId: number | string,
    cardholderId: number | string,
    personCode: string,
    relationshipCode: RelationshipCode,
    helpdeskNumber: string,
    coverageType: string;
    effectiveDate: Date,
    terminationDate: Date,
    active: boolean;
    /**
     * True once the plan has been removed from the patient. Distinct from `active`, which marks a
     * plan as temporarily switched off but still assigned. Read-only: removal goes through the
     * remove endpoint, not the plan save.
     */
    deleted?: boolean;
    rank?: number | null;
    planName?: string | null;
    planBin?: string | null;
    planPcn?: string | null;
    pbmName?: string | null;
    dateOfBirthOverride?: Date | string | null;
    /**
     * Save-intent flag: when true the backend explicitly clears dateOfBirthOverride.
     * Necessary because the save proc otherwise preserves the existing override when
     * dateOfBirthOverride is null (so partial-payload callers don't wipe it).
     */
    clearDateOfBirthOverride?: boolean;
    eligibilityClarificationCode?: EligibilityClarificationCode | null;
    /**
     * Save-intent flag: when true the backend explicitly clears eligibilityClarificationCode.
     * Same rationale as clearDateOfBirthOverride.
     */
    clearEligibilityClarificationCode?: boolean;
    /**
     * Date of Injury sent on the Workers Comp segment of an outbound claim. Auto-submitted on
     * every claim billed against this plan when set; a manual override on the billing screen
     * takes precedence.
     */
    dateOfInjury?: Date | string | null;
    /**
     * Save-intent flag: when true the backend explicitly clears dateOfInjury.
     * Same rationale as clearDateOfBirthOverride.
     */
    clearDateOfInjury?: boolean;
}

/**
 * Normalizes an edited patient plan against the stored one before saving, setting the three
 * save-intent clear flags.
 *
 * The datepicker's reset button emits "" rather than null, which fails System.Text.Json binding on
 * the nullable dates and 400s the request. The save proc also preserves a stored value when it
 * receives null (so partial-payload callers can't wipe it), so an explicit clear has to be
 * announced separately via the matching flag.
 *
 * Every flag is assigned unconditionally rather than only being set: a failed save leaves the edit
 * modal open holding this same object, so a flag left over from a previous attempt would ride along
 * with a newly entered value and the proc would null the column instead.
 */
export function applyPatientPlanClearFlags(edited: PatientPlan, stored?: PatientPlan | null) {
    edited.clearDateOfBirthOverride = !edited.dateOfBirthOverride && !!stored?.dateOfBirthOverride;
    edited.dateOfBirthOverride = edited.dateOfBirthOverride || null;

    edited.clearDateOfInjury = !edited.dateOfInjury && !!stored?.dateOfInjury;
    edited.dateOfInjury = edited.dateOfInjury || null;

    // The eligibility clarification code needs no normalization; its selector emits undefined once
    // cleared, so compare loosely.
    edited.clearEligibilityClarificationCode =
        edited.eligibilityClarificationCode == null && stored?.eligibilityClarificationCode != null;
}

export enum RelationshipCode {
    NotSpecified = 0,
    Cardholder,
    Spouse,
    Child,
    Other,
}

/**
 * NCPDP field 309-C9, sent on the Insurance segment of an outbound claim.
 * NotSpecified is part of the NCPDP value set but is hidden from every selector:
 * leaving the field empty is how "no override" is expressed, so transmitting an
 * explicit 0 would assert something different to the payor.
 */
export enum EligibilityClarificationCode {
    NotSpecified = 0,
    NoOverride = 1,
    Override = 2,
    FullTimeStudent = 3,
    DisabledDependent = 4,
    DependentParent = 5,
    SignificantOther = 6,
    NonHuman = 7,
}

export class PatientPlan extends HasLabel {
    constructor (ptPlan?: PatientPlan) {
        super();
        this.groupId = '';
        this.cardholderId = '';
        this.personCode = '';
        this.relationshipCode = RelationshipCode.Cardholder;
        if (ptPlan) {
            Object.assign(this, ptPlan);
        }
        if (ptPlan?.active === null || ptPlan?.active === undefined) this.active = true;
    }
    toString(): string {
        const text = [];

        if (this.planName) {
            text.push("<strong>" + this.planName + "</strong>");
        }

        if (this.groupId) {
            text.push("Group: " + this.groupId);
        }

        if (this.cardholderId) {
            text.push("Card: " + this.cardholderId);
        }
        
        if (!text.length && this.id) {
            return "Patient Plan #" + this.id;
        }

        return text.join(", ");
    }

    get relationshipDesc():string {
        return PatientPlan.RelationshipToString(this.relationshipCode);
    }

    public static RelationshipToString(num:RelationshipCode):string{
        if(isNaN(num) || num == null) return '';
        let res = RelationshipCode[num];
        res = TextUtils.camelCaseToNormal(res);

        return res;
    }
}

export enum PriorAuthorizationTypeCode {
    NotSpecified = 0,
    PriorAuthorization,
    MedicalCertification,
    EPSDT,
    ExemptionFromCopayAndOrCoinsurance,
    ExemptionFromRx,
    FamilyPlanningIndicator,
    TANF,
    PayerDefinedExemption,
    EmergencyPreparedness

}

export enum SubmissionClarificationCode {

    NoOverride = 1,
    OtherOverride = 2,
    VacationSupply = 3,
    LostDamagedPrescription = 4,
    TherapyChange = 5,
    StarterDose = 6,
    MedicallyNecessary = 7,
    ProcessCompoundForApprovedIngredients = 8,
    Encounters = 9,
    MeetsPlanLimitations = 10,
    CertificationOnFile = 11,
    DMEReplacementIndicator = 12,
    // PayerRecognizedEmergencyDisasterAssistanceRequest = 13,
    LongTermCareLeaveofAbsence = 14,
    LongTermCareReplacementMedication = 15,
    LongTermCareEmergencyBoxKitOrAutomatedDispensingMachine = 16,
    LongTermCareEmergencySupplyRemainder = 17,
    LongTermCarePatientAdmitReadmitIndicator = 18,
    SplitBilling = 19,
    Plan340B = 20,
    LTCDispensing14DaysOrLessNotApplicable = 21,
    LTCDispensing7days = 22,
    LTCDispensing4days = 23,
    LTCDispensing3days = 24,
    LTCDispensing2days = 25,
    LTCDispensing1day = 26,
    LTCDispensing4Then3Days = 27,
    LTCDispensing2Then2Then3Days = 28,
    LTCDispensingDailyAnd3DayWeekend = 29,
    LTCDispensingPerShiftDispensing = 30,
    LTCDispensingPerMedpassDispensing = 31,
    LTCDispensingPRNOnDemand = 32,
    LTCDispensing7DayOrLessCycleNotOtherwiseRepresented = 33,
    LTCDispensing14DaysDispensing = 34,
    LTCDispensing8To14DayDispensingMethodNotListedAbove = 35,
    LTCDispensingDispensedOutsideShortCycle = 36,

    PrescriberIDSubmittedIsValidAndPrescribingRequirementsHaveBeenValidated = 42,
    PrescribersDEAIsActiveWithDEAAuthorizedPrescriptiveRight = 43,

    PrescribersDEAIsAValidHospitalDEAWithSuffixAndHasPrescriptiveAuthorityForThisDrugDEASchedule = 45,
    PrescribersDEAHasPrescriptiveAuthorityForThisDrugDEASchedule = 46,
    ShortenedDaysSupplyFill = 47,
    FillSubsequentToAShortenedDaysSupplyFill = 48,
    PrescriberDoesNotCurrentlyHaveAnActiveType1NPI = 49,
    PrescribersActiveMedicareFeeForServiceEnrollmentStatusHasBeenValidated = 50,
    PharmacysActiveMedicareFeeForServiceEnrollmentStatusHasBeenValidated = 51,
    PrescribersStateLicenseWithPrescriptiveAuthorityHasBeenValidated = 52,
    PrescriberNPIActiveAndValid = 53,
    CMSOtherAuthorizedPrescriberOAP = 54,
    Other = 99,
}

export enum OtherCoverageCode {
    NotSpecifiedByPatient = 0,
    NoOtherCoverage = 1,
    OtherCoverageExistsPaymentIndicated = 2,
    OtherCoverageBilledClaimRejected = 3,
    OtherCoverageExistsNoPaymentIndicated = 4,
    ClaimIsBillingForPatientFinancialResponsibilityOnly = 8,
}
